/**
 * "If care needs increase" (rebuild M4, 19 Oct 2026; review decision 7, the mock-up's j1-08b; pack
 * B.SAH.7). Two things that often happen later, each tested with the plan's choices still in place,
 * against the support alone under the same change: the person's needs step up a classification, and they
 * move into residential care. For the person whose aged care is on file first; a default year 3 the
 * family can change; information only — it never changes which plan leads.
 *
 * Priced on the same household and options as the plan's card (sahEngineContext.ts). A plan built by
 * hand sends its own position (`planFactFind`) and is tested as "change nothing further".
 */
import React, { useEffect, useMemo, useState } from 'react';
import DialogShell, { DialogSection } from '../../components/ui/DialogShell.tsx';
import { Button } from '../../components/ui/index.tsx';
import * as calculationApi from '../../services/calculationApi.ts';
import { formatCurrency } from '../../utils/index.ts';
import { ACAT_LEVEL_DATA } from '../../../../shared/src/calculation/constants/acatLevelData.ts';
import { extractAgedCareIntegrationDetails, type SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';
import { sahEngineContext, sahEnteringPerson, sahMoveAnswer, sahPricedServicePlan, chosenAgedCarePlanOf, linkedResidencyOverridesOf } from './sahEngineContext.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { useSahLinkedPosition } from './useSahLinkedPosition.ts';

export interface SahCareUpDialogProps {
    factFind: DraftFactFind;
    setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    servicePlan: { client?: Record<string, unknown>; partner?: Record<string, unknown> };
    years: number;
    supportStartDate?: string;
    savedStrategies: ReadonlyArray<SavedStrategy>;
    planTitle: string;
    /** An engine plan's config; null for a plan built by hand (then `planFactFind`). */
    strategyConfig: unknown;
    planFactFind?: DraftFactFind | null;
    onClose: () => void;
}

const money = (n: number) => formatCurrency(Math.abs(Math.round(n)));
const yearWords = (cf: number) => (cf < 0 ? `short ${money(cf)}` : `${money(cf)} to spare`);
const classWords = (level?: string) => {
    const label = level ? String(ACAT_LEVEL_DATA[level]?.label || '') : '';
    return label.match(/classification\s*(\d+)/i)?.[1] ?? '';
};

const Pair = ({ label, sub, plan, nothing, bold = false }: { label: string; sub?: string; plan: string; nothing: string; bold?: boolean }) => (
    <div className={`flex items-start justify-between gap-6 ${bold ? 'mt-1 border-t-2 border-brand-navy pt-3.5' : 'border-b border-slate-100 py-3'}`}>
        <div className="min-w-0">
            <p className={bold ? 'text-[16px] font-bold text-brand-navy' : 'text-[15px] text-slate-800'}>{label}</p>
            <p className="mt-0.5 text-[13px] text-slate-500">{sub ?? 'with this plan · with the support alone'}</p>
        </div>
        <p className={`shrink-0 text-right tabular-nums ${bold ? 'text-[17px] font-bold text-brand-navy' : 'text-[15px] font-semibold text-slate-900'}`}>
            {plan} <span className="font-normal text-slate-400">·</span> <span className="font-normal text-slate-500">{nothing}</span>
        </p>
    </div>
);

const SahCareUpDialog = ({ factFind, setFactFind, servicePlan, years, supportStartDate, savedStrategies, planTitle, strategyConfig, planFactFind = null, onClose }: SahCareUpDialogProps) => {
    // Already moving (step 1: "Yes"): the move is in every figure, so the step up is for the one at home.
    const alreadyMoving = sahMoveAnswer(factFind) === 'yes';
    const entering = sahEnteringPerson(factFind) ?? 'client';
    const person: 'client' | 'partner' = alreadyMoving ? (entering === 'client' ? 'partner' : 'client') : entering;
    const other: 'client' | 'partner' = person === 'client' ? 'partner' : 'client';
    const name = (k: 'client' | 'partner') => String((k === 'client' ? factFind?.client?.firstName : factFind?.partner?.firstName) || (k === 'client' ? 'the client' : 'the partner'));
    const isCouple = factFind?.adviceFor === 'couple';
    const v = householdVoice(factFind);
    const savedYear = Number((factFind?.goals as { sahCareUpYear?: number } | undefined)?.sahCareUpYear) || 3;
    const [year, setYearState] = useState(Math.max(1, Math.min(years, savedYear)));
    const setYear = (y: number) => {
        setYearState(y);
        setFactFind?.((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), sahCareUpYear: y } }));
    };
    // Already moving: priced on the whole aged care plan carried out, as steps 1 and 2 are (24 Oct 2026).
    const position = useSahLinkedPosition(factFind, alreadyMoving ? chosenAgedCarePlanOf(savedStrategies as SavedStrategy[]) : null);
    const ctx = useMemo(() => sahEngineContext({ factFind, savedStrategies: savedStrategies as SavedStrategy[], supportStartDate, positioned: position.positioned }), [factFind, savedStrategies, supportStartDate, position.positioned]);
    const linked = useMemo(() => {
        const record = chosenAgedCarePlanOf(savedStrategies as SavedStrategy[]);
        const d = record ? extractAgedCareIntegrationDetails(record) : null;
        if (!d) return null;
        return {
            radFunding: d.radFunding || null,
            radPaid: Number(d.radPaid) || 0,
            annualCost: Number(d.annualCost) || 0,
            livingExpenses: d.livingExpenses ?? null,
            residencyOverrides: linkedResidencyOverridesOf(d, factFind),
        };
    }, [savedStrategies, factFind]);
    const [result, setResult] = useState<Record<string, any> | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setResult(null);
        setError(null);
        if (!position.ready) return () => { cancelled = true; };
        calculationApi.sahCareUpTests({
            factFind: ctx.baselineFactFind,
            servicePlan: sahPricedServicePlan(servicePlan, factFind) as Record<string, unknown>,
            legacyYears: years,
            strategyConfig: strategyConfig ?? { type: 'doNothing', name: 'Do Nothing' },
            event: { person, year },
            linked: alreadyMoving ? null : (linked as Record<string, unknown> | null),
            evaluationOptions: ctx.evaluationOptions as Record<string, unknown>,
            // Sent only for a plan built by hand: the request schema checks it as a household.
            ...(planFactFind ? { planFactFind } : {}),
        })
            .then((r: Record<string, any>) => { if (!cancelled) setResult(r); })
            .catch(() => { if (!cancelled) setError('These tests could not be run just now.'); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, position.ready, position.positioned]);

    const s = result?.stepUp;
    const personHasServices = Object.values((sahPricedServicePlan(servicePlan, factFind) as Record<string, Record<string, { quantity?: unknown } | undefined> | undefined>)?.[person] || {})
        .some((d) => Number(d?.quantity) > 0);
    const m = result?.move;
    const yearPicker = (
        <label className="ml-1 inline-flex items-center gap-1 text-[13px]">
            <span className="sr-only">The year</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[13px]" data-testid="sah-careup-year">
                {Array.from({ length: years }, (_v, i) => i + 1).map((y) => <option key={y} value={y}>year {y}</option>)}
            </select>
        </label>
    );

    return (
        <DialogShell
            titleId="sah-careup-heading"
            testId="sah-careup"
            eyebrow={planTitle}
            title="If care needs increase"
            intro="Two things that often happen later. Each is tested with this plan's choices still in place, against the support alone under the same change. The year can be changed."
            onClose={onClose}
            footer={<Button type="button" variant="primary" onClick={onClose}>Got it</Button>}
        >
            {error && <p className="text-[14px] text-rose-700" role="alert">{error}</p>}
            {!result && !error && <p className="text-[14px] text-slate-600">Running the two tests…</p>}

            {result && (
                <div className="space-y-6">
                    <section data-testid="sah-careup-stepup">
                        <DialogSection>{name(person)}&apos;s needs step up in {yearPicker}</DialogSection>
                        {s?.plan?.applicable && !personHasServices ? (
                            <p className="mt-2 text-[14px] text-slate-600" data-testid="sah-careup-no-services">
                                {name(person)} has no services in the plan yet, so a step up changes nothing here. Add {name(person)}&apos;s services on step 1 and this test prices the bigger classification.
                            </p>
                        ) : s?.plan?.applicable ? (
                            <>
                                <p className="mt-2 text-[13px] leading-relaxed text-slate-600">
                                    Classification {classWords(s.plan.from)} to {classWords(s.plan.to)}: a budget of {money(s.plan.newBudget)} a year, services scaled up to match it, the same contribution rates, and anything above the budget at full price. Their yearly cost goes from {money(s.plan.yearlyBefore)} to {money(s.plan.yearlyAfter)} with this plan.
                                </p>
                                <Pair label="Each year after the change" plan={yearWords(s.plan.cashflowAfter)} nothing={yearWords(s.doNothing.cashflowAfter)} />
                                <Pair label={`Left to the family after ${years} years`} plan={money(s.plan.legacy)} nothing={money(s.doNothing.legacy)} />
                                {s.plan.lifetimeLimitYear != null && (
                                    <p className="mt-2 text-[13px] text-slate-600">The lifetime limit on contributions is reached in year {s.plan.lifetimeLimitYear}; after that they pay only for services above the budget.</p>
                                )}
                            </>
                        ) : (
                            <p className="mt-2 text-[14px] text-slate-600">
                                {s?.plan?.reason === 'highest' ? `${name(person)} is already at the highest classification, so there is no step up to test.` : `${name(person)} has no classification recorded, so a step up cannot be priced.`}
                            </p>
                        )}
                    </section>

                    <section data-testid="sah-careup-move">
                        <DialogSection>{alreadyMoving ? `${name(entering)}'s move into residential care` : <>{name(person)} moves into residential care in {yearPicker}</>}</DialogSection>
                        {alreadyMoving ? (
                            <p className="mt-2 text-[14px] text-slate-600">{name(entering)}&apos;s move is already in every figure (step 1 says {name(entering)} is moving), so there is nothing further to test here.</p>
                        ) : m?.plan?.applicable ? (
                            <>
                                <p className="mt-2 text-[13px] leading-relaxed text-slate-600">
                                    Priced on {name(person)}&apos;s chosen aged care plan: its deposit, living costs and yearly fees{isCouple ? `, with ${name(other)} staying at home with support and the two of ${v.them} assessed as an illness-separated couple from the move` : ''}.
                                </p>
                                <Pair
                                    label={`Can ${v.they} pay the room deposit without selling anything?`}
                                    sub={`${money(m.plan.cashAtMove)} in the bank at the move, against a ${money(m.plan.deposit)} deposit. With the support alone: ${money(m.doNothing.cashAtMove)}.`}
                                    plan={m.plan.canPayFromCash ? 'Yes' : 'No'}
                                    nothing={m.doNothing.canPayFromCash ? 'yes' : 'no'}
                                />
                                <Pair label="Each year after the move" plan={yearWords(m.plan.cashflowAfter)} nothing={yearWords(m.doNothing.cashflowAfter)} />
                                <Pair bold label={`Left to the family after ${years} years`} plan={money(m.plan.legacy)} nothing={money(m.doNothing.legacy)} />
                            </>
                        ) : (
                            <p className="mt-2 text-[14px] text-slate-600">Choose {name(person)}&apos;s aged care plan first; this test is priced on its room, deposit and fees.</p>
                        )}
                    </section>

                    <p className="text-[13px] text-slate-500">This is for information. It does not change which plan leads.</p>
                </div>
            )}
        </DialogShell>
    );
};

export default SahCareUpDialog;
